import { PURCHASE_REASONS, PurchaseReasonIcon } from './PaymentReasons';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './PaymentAnalytics.css';

export type ReasonCount = {
  reason: string;
  buyers: number;
  selections: number;
  prices?: { amount: string; buyers: number }[];
};
export type PriceRevenue = { amount: string; revenue: string; buyers: number };
const colors = ['#63b3ed', '#f6ad55', '#b794f4', '#68d391', '#fc8181', '#76e4f7'];

export function PaymentAnalytics({
  total,
  currency,
  prices,
  reasons = [],
  showReasons = true,
  title,
  revenueByAudience,
}: {
  total: string;
  currency: string;
  prices: PriceRevenue[];
  reasons?: ReasonCount[];
  showReasons?: boolean;
  title?: string;
  revenueByAudience?: { organizers: string; gifts: string; random: string };
}) {
  const { t, i18n } = useTranslation();
  const popupId = useId();
  const reasonTrigger = useRef<HTMLButtonElement | null>(null);
  const reasonPopup = useRef<HTMLDivElement>(null);
  const [reasonPopupOpen, setReasonPopupOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  useLayoutEffect(() => {
    if (!reasonPopupOpen) return;
    const position = () => {
      if (!reasonTrigger.current || !reasonPopup.current) return;
      const anchor = reasonTrigger.current.getBoundingClientRect();
      const sheet = reasonPopup.current.getBoundingClientRect();
      const left = Math.max(16, Math.min(anchor.left, window.innerWidth - sheet.width - 16));
      const below = anchor.bottom + 8;
      const top =
        below + sheet.height <= window.innerHeight - 16
          ? below
          : Math.max(16, anchor.top - sheet.height - 8);
      reasonPopup.current.style.left = `${left}px`;
      reasonPopup.current.style.top = `${top}px`;
    };
    position();
    const observer = new ResizeObserver(position);
    if (reasonPopup.current) observer.observe(reasonPopup.current);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [reasonPopupOpen, selectedReason]);
  const money = (value: string) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency }).format(
      Number(value || 0),
    );
  const revenue = Number(total || 0);
  const active = prices.find((price) => price.amount === selected);
  const reasonCounts = new Map(reasons.map((item) => [item.reason, item]));
  const rankedReasons = [...new Set<string>([...PURCHASE_REASONS, ...reasonCounts.keys()])]
    .map((reason) => ({ reason, buyers: reasonCounts.get(reason)?.buyers ?? 0, selections: reasonCounts.get(reason)?.selections ?? 0 }))
    .sort((a, b) => b.selections - a.selections || b.buyers - a.buyers);
  return (
    <section className="payment-analytics" aria-label={(title ?? t('admin.payments.revenueByPrice'))}>
      <RevenueSources values={revenueByAudience} currency={currency} />
      <div className="payment-analytics__category">
      <div className="payment-analytics__donut">
        <svg viewBox="0 0 200 200" aria-label={(title ?? t('admin.payments.revenueByPrice'))}>
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke="currentColor"
            opacity="0.15"
            strokeWidth="24"
          />
          {prices.map((price, index) => {
            const share = revenue > 0 ? (Number(price.revenue) / revenue) * 100 : 0;
            const start =
              revenue > 0
                ? (prices.slice(0, index).reduce((sum, item) => sum + Number(item.revenue), 0) /
                    revenue) *
                  100
                : 0;
            if (share <= 0) return null;
            return (
              <circle
                key={price.amount}
                cx="100"
                cy="100"
                r="80"
                pathLength="100"
                fill="none"
                stroke={colors[index % colors.length]}
                strokeWidth={active?.amount === price.amount ? 30 : 24}
                strokeDasharray={`${share} ${100 - share}`}
                strokeDashoffset={-start}
                transform="rotate(-90 100 100)"
                role="button"
                tabIndex={0}
                aria-pressed={active?.amount === price.amount}
                aria-label={`${money(price.amount)}: ${money(price.revenue)}, ${t('admin.payments.buyers')}: ${price.buyers}`}
                onClick={() => setSelected(price.amount)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelected(price.amount);
                  }
                }}
              >
                <title>
                  {money(price.amount)} · {money(price.revenue)}
                </title>
              </circle>
            );
          })}
        </svg>
        <div className="payment-analytics__center" data-testid="admin-payments-total">
          <span>{t('admin.payments.totalPaid')}</span>
          <strong>{money(total)}</strong>
        </div>
      </div>
      <div className="payment-analytics__body">
        <h3>{(title ?? t('admin.payments.revenueByPrice'))}</h3>
        <p>{t(revenue > 0 ? 'admin.payments.selectPrice' : 'admin.payments.noPaidRevenue')}</p>
        <div className="payment-analytics__legend">
          {prices.map((price, index) => (
            <button
              type="button"
              key={price.amount}
              aria-pressed={active?.amount === price.amount}
              onClick={() => setSelected(price.amount)}
            >
              <span
                className="payment-analytics__swatch"
                style={{ background: colors[index % colors.length] }}
              />
              {money(price.amount)}{' '}
              <small>
                {new Intl.NumberFormat(i18n.language, {
                  style: 'percent',
                  maximumFractionDigits: 1,
                }).format(revenue > 0 ? Number(price.revenue) / revenue : 0)}
              </small>
            </button>
          ))}
        </div>
        <div
          className="payment-analytics__selection"
          aria-live="polite"
          data-testid="admin-price-details"
        >
          {active && (
            <>
              <strong>{money(active.amount)}</strong>
              <span>
                {t('admin.payments.buyers')}: {active.buyers}
              </span>
              <span>
                {t('admin.payments.priceRevenue')}: {money(active.revenue)}
              </span>
            </>
          )}
        </div>
      </div>
      {showReasons && <section
        className="payment-analytics__reasons"
        aria-labelledby="purchase-reasons-title"
        data-testid="admin-reason-summary"
      >
        <h3 id="purchase-reasons-title">{t('admin.payments.reasonsTitle')}</h3>
        <p>{t('admin.payments.reasonsHint')}</p>
        <ul>
          {rankedReasons.map(({ reason, buyers, selections }) => (
            <li key={reason}>
              <button
                type="button"
                popoverTarget={popupId}
                aria-haspopup="dialog"
                onClick={(event) => {
                  reasonTrigger.current = event.currentTarget;
                  setSelectedReason(reason);
                }}
                className={`admin-payments__reason admin-payments__reason--${PURCHASE_REASONS.includes(reason as (typeof PURCHASE_REASONS)[number]) ? reason : 'other'}`}
              >
                <PurchaseReasonIcon reason={reason} />{' '}
                {t(`admin.payments.reasonLabels.${reason}`, { defaultValue: reason })}
                {' — '}
                <strong>{new Intl.NumberFormat(i18n.language).format(buyers)} — {new Intl.NumberFormat(i18n.language).format(selections)}</strong>
              </button>
            </li>
          ))}
        </ul>
        <div
          id={popupId}
          ref={reasonPopup}
          onToggle={(event) => setReasonPopupOpen(event.newState === 'open')}
          popover="auto"
          role="dialog"
          aria-labelledby={`${popupId}-title`}
          className="admin-payments__survey payment-analytics__reason-prices"
        >
          <div className="admin-payments__survey-heading">
            <h3 id={`${popupId}-title`}>
              <PurchaseReasonIcon reason={selectedReason ?? 'other'} />{' '}
              {t(`admin.payments.reasonLabels.${selectedReason}`, {
                defaultValue: selectedReason ?? '',
              })}
            </h3>
            <button
              type="button"
              popoverTarget={popupId}
              popoverTargetAction="hide"
              aria-label={t('admin.payments.closeSurvey')}
            >
              ×
            </button>
          </div>
          <h4>{t('admin.payments.reasonPrices')}</h4>
          {reasons.find((item) => item.reason === selectedReason)?.prices?.length ? (
            <ul className="payment-analytics__reason-price-list">
              {reasons
                .find((item) => item.reason === selectedReason)!
                .prices!.map((price) => (
                  <li key={price.amount}>
                    <span
                      className="admin-payments__reason payment-analytics__price-badge"
                      style={{
                        backgroundColor:
                          colors[
                            Math.max(
                              0,
                              prices.findIndex(
                                (item) => Number(item.amount) === Number(price.amount),
                              ),
                            ) % colors.length
                          ],
                      }}
                    >
                      {money(price.amount)}
                      {' — '}
                      <strong>{new Intl.NumberFormat(i18n.language).format(price.buyers)}</strong>
                    </span>
                  </li>
                ))}
            </ul>
          ) : (
            <p>{t('admin.payments.noReasonPrices')}</p>
          )}
        </div>
      </section>}
      </div>
    </section>
  );
}

function RevenueSources({values,currency}: {values?: {organizers:string;gifts:string;random:string};currency:string}) {
  const {i18n}=useTranslation();
  const ru=i18n.language.startsWith('ru');
  const rows=[
    {key:'organizers' as const,label:ru?'Организаторы':'Organizers',color:'#63b3ed'},
    {key:'gifts' as const,label:ru?'Подарки подопечным':'Gifts to giftees',color:'#b794f4'},
    {key:'random' as const,label:ru?'Анонимные подарки':'Anonymous gifts',color:'#68d391'},
  ];
  const total=rows.reduce((sum,row)=>sum+Number(values?.[row.key]??0),0);
  const money=(value:number)=>new Intl.NumberFormat(i18n.language,{style:'currency',currency}).format(value);
  const percent=(value:number)=>new Intl.NumberFormat(i18n.language,{style:'percent',maximumFractionDigits:1}).format(total>0?value/total:0);
  let offset=0;
  return <section className="payment-analytics__sources" data-testid="revenue-sources" aria-label={ru?'Общая выручка':'Overall revenue'}>
    <h3>{ru?'Общая выручка':'Overall revenue'}</h3>
    <p>{ru?'Все категории · По выбранным фильтрам':'All categories · Selected filters'}</p>
    <div className="payment-analytics__donut">
      <svg viewBox="0 0 200 200" aria-hidden="true">
        <circle cx="100" cy="100" r="80" fill="none" stroke="currentColor" opacity=".15" strokeWidth="24" />
        {rows.map(row=>{const share=total>0?Number(values?.[row.key]??0)/total*100:0;const start=offset;offset+=share;return <circle key={row.key} cx="100" cy="100" r="80" pathLength="100" fill="none" stroke={row.color} strokeWidth="24" strokeDasharray={`${share} ${100-share}`} strokeDashoffset={-start} transform="rotate(-90 100 100)" />;})}
      </svg>
      <div className="payment-analytics__center"><span>{ru?'Всего оплачено':'Total paid'}</span><strong>{values?money(total):'—'}</strong></div>
    </div>
    <ul>{rows.map(row=><li key={row.key} data-audience={row.key}><span className="payment-analytics__swatch" style={{background:row.color}} /><span>{row.label}<strong>{values?money(Number(values[row.key])):'—'}</strong></span><small>{values?percent(Number(values[row.key])):'—'}</small></li>)}</ul>
  </section>;
}
